import React, { useEffect, useRef, useCallback } from "react";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
// Forms only wait for a token when the widget is configured.
export const TURNSTILE_ON = !!SITE_KEY;

let scriptPromise = null;
function loadScript() {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Turnstile failed to load"));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

/**
 * Cloudflare Turnstile widget (explicit render). Calls onVerify(token) on
 * success and onVerify("") when the token expires or errors.
 */
export default function Turnstile({ onVerify, onError, action }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const cbRef = useRef(onVerify);
  cbRef.current = onVerify;
  const errRef = useRef(onError);
  errRef.current = onError;

  const render = useCallback(() => {
    if (!window.turnstile || !containerRef.current || widgetIdRef.current !== null) return;
    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: SITE_KEY,
      ...(action ? { action } : {}),
      callback: (token) => { errRef.current && errRef.current(""); cbRef.current && cbRef.current(token); },
      "expired-callback": () => cbRef.current && cbRef.current(""),
      "error-callback": (code) => {
        cbRef.current && cbRef.current("");
        errRef.current && errRef.current(String(code || "unknown"));
        return true; // handled: we show our own message
      },
    });
  }, [action]);

  useEffect(() => {
    let mounted = true;
    if (!SITE_KEY) return;
    loadScript()
      .then(() => {
        if (!mounted) return;
        if (window.turnstile) render();
      })
      .catch(() => errRef.current && errRef.current("script"));
    return () => {
      mounted = false;
      if (window.turnstile && widgetIdRef.current !== null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          /* noop */
        }
        widgetIdRef.current = null;
      }
    };
  }, [render]);

  if (!SITE_KEY) return null;
  return <div ref={containerRef} data-testid="turnstile-widget" className="my-2" />;
}

/**
 * Invisible-unless-needed Turnstile for a form: `await getToken()` on submit
 * (fresh single-use token each call; "" when Turnstile is off). If Cloudflare
 * needs an interactive check it appears in a small fixed box at the bottom.
 */
export function useTurnstile(action) {
  const idRef = useRef(null);
  const boxRef = useRef(null);
  const waitRef = useRef(null);

  useEffect(() => () => {
    if (window.turnstile && idRef.current !== null) { try { window.turnstile.remove(idRef.current); } catch (e) { /* noop */ } }
    if (boxRef.current) boxRef.current.remove();
    idRef.current = null; boxRef.current = null;
  }, []);

  const finish = (t) => { const w = waitRef.current; waitRef.current = null; if (w) w(t); };

  return useCallback(() => {
    if (!SITE_KEY) return Promise.resolve("");
    return new Promise((resolve) => {
      waitRef.current = resolve;
      setTimeout(() => { if (waitRef.current === resolve) finish(""); }, 60000);
      loadScript().then(() => {
        if (!boxRef.current) {
          const box = document.createElement("div");
          box.setAttribute("data-testid", `turnstile-${action}`);
          box.style.cssText = "position:fixed;left:50%;bottom:16px;transform:translateX(-50%);z-index:2147483000";
          document.body.appendChild(box);
          boxRef.current = box;
        }
        if (idRef.current === null) {
          idRef.current = window.turnstile.render(boxRef.current, {
            sitekey: SITE_KEY, action, execution: "execute", appearance: "interaction-only",
            callback: (t) => finish(t),
            "error-callback": () => { finish(""); return true; },
          });
        } else {
          window.turnstile.reset(idRef.current);
        }
        window.turnstile.execute(idRef.current);
      }).catch(() => finish(""));
    });
  }, [action]);
}
